import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  collection,
  doc,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import Swal from 'sweetalert2';
import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';
import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';
import ICategory from '~/interfaces/ICategory';
import IComplement from '~/interfaces/IComplement';
import getValidationErrors from '~/utils/getValidationErrors';

interface FormData {
  complement_id: string;
}

interface Params {
  id: string;
}

const CategoriesComplements: React.FC = () => {
  const params = useParams<Params>();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [category, setCategory] = useState<ICategory>({} as ICategory);
  const [complements, setComplements] = useState<IComplement[]>([]);
  const [categoryComplements, setCategoryComplements] = useState<IComplement[]>(
    [],
  );
  const [data, setData] = useState({} as FormData);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Todos');
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);
  const { id } = params;

  useEffect(() => {
    async function loadCategory() {
      const categoriesRef = doc(DB, 'categories', id);
      const docSnap = await getDoc(categoriesRef);
      const item = docSnap.data() as ICategory;

      if (item.complements && item.complements.length > 0) {
        const q = query(
          collection(DB, 'complements'),
          where('user_id', '==', user.uid),
          where('uid', 'in', item.complements),
        );
        const querySnapshot = await getDocs(q);

        const items: IComplement[] = [];

        querySnapshot.docs.forEach(item => {
          const parseItem = {
            ...item.data(),
            uid: item.id,
          } as IComplement;

          items.push(parseItem);
        });

        setCategoryComplements(items);
      } else {
        setCategoryComplements([]);
      }

      setCategory({
        ...item,
        uid: id,
      } as ICategory);
    }

    if (id) loadCategory();
  }, [id, user]);

  const loadComplements = useCallback(async () => {
    const categoriesRef = collection(DB, 'complements');
    const q = query(categoriesRef, where('user_id', '==', user.uid));
    const querySnapshot = await getDocs(q);

    const items: IComplement[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as IComplement;

      items.push(parseItem);
    });

    setComplements(items);
  }, [user]);

  useEffect(() => {
    loadComplements();
  }, [loadComplements]);

  const filteredCategories = useMemo(() => {
    const filtered = categoryComplements.filter(item => {
      let result = true;

      if (status === 'Ativo') {
        result =
          item.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) &&
          item.status === true;
      } else if (status === 'Inativo') {
        result =
          item.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) &&
          item.status === false;
      } else {
        result = item.name
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase());
      }

      return result;
    });
    return filtered;
  }, [search, categoryComplements, status]);

  const handleConfirmRemove = useCallback(
    async (uid: string) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão da categoria?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        const items = [...category.complements];
        const items2 = [...categoryComplements];

        const findIndex = items.findIndex(find => find === uid);
        const findIndex2 = items2.findIndex(find => find.uid === uid);

        if (findIndex !== -1 && findIndex2 !== -1) {
          items.splice(findIndex, 1);
          items2.splice(findIndex2, 1);

          await updateDoc(doc(DB, 'categories', id), {
            complements: items,
          });

          setCategoryComplements(items2);
        }

        addToast({
          type: 'success',
          description: 'Grupo de complementos foi removido com sucesso.',
        });
      }
    },
    [addToast, category, id, categoryComplements],
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const schema = Yup.object().shape({
          complement_id: Yup.string().required('O complemento é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { complement_id } = data;

        const findItem = complements.find(find => find.uid === complement_id);

        // const updateComplements = category.complements
        //   ? [...category.complements]
        //   : [];

        const updateComplements = categoryComplements.map(item => item.uid);

        updateComplements.push(complement_id);

        await updateDoc(doc(DB, 'categories', id), {
          complements: updateComplements,
        });

        if (findItem) {
          setCategoryComplements(current => [...current, findItem]);
        }

        addToast({
          type: 'success',
          description: 'Complemento vinculado com sucesso!',
        });

        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [data, addToast, id, complements, categoryComplements],
  );

  return (
    <Dashboard containerMessage="col-xl-8 offset-xl-2">
      <div className="row">
        <div className="col-xl-8 offset-xl-2 mb-4">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/categories">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">Vincular Complementos</h4>
                <div className="nk-block-des">
                  <p>
                    Selecione um grupo de complementos para adicionar na
                    categoria.
                  </p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label" htmlFor="default-06">
                          Grupo de Complementos
                        </label>
                        <div className="form-control-wrap ">
                          <div className="form-control-select">
                            <select
                              className="form-control"
                              id="default-06"
                              defaultValue={data.complement_id}
                              onChange={event => {
                                setData({
                                  ...data,
                                  complement_id: event.target.value,
                                });
                              }}
                            >
                              <option value="">Selecione...</option>
                              {complements.map(item => (
                                <option value={item.uid} key={item.uid}>
                                  {`${item.name} ${item.name2 ? '/' : ''} ${
                                    item.name2 || ''
                                  }`}
                                </option>
                              ))}
                            </select>
                            {errors.complement_id && (
                              <span className="invalid">
                                {errors.complement_id}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className="col-sm-6 d-flex"
                      style={{ alignItems: 'flex-end' }}
                    >
                      <div className="form-group mb-3">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Adicionar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-8 offset-xl-2">
          <div className="nk-block-head nk-block-head-sm">
            <div className="nk-block-between">
              <div className="nk-block-head-content">
                <h6 className="nk-block-title">{category.name}</h6>
              </div>
              <div className="nk-block-head-content">
                <div className="toggle-wrap nk-block-tools-toggle">
                  <button
                    type="button"
                    className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                      showFilter && 'active'
                    }`}
                    data-target="pageMenu"
                    onClick={() => setShowFilter(current => !current)}
                  >
                    <em className="icon ni ni-more-v" />
                  </button>
                  <div
                    className={`toggle-expand-content ${
                      showFilter && 'expanded'
                    }`}
                    data-content="pageMenu"
                  >
                    <ul className="nk-block-tools g-3">
                      <li>
                        <div className="form-control-wrap">
                          <div className="form-icon form-icon-right">
                            <em className="icon ni ni-search" />
                          </div>
                          <input
                            type="text"
                            className="form-control"
                            id="default-04"
                            placeholder="Buscar por categoria"
                            onChange={event => {
                              setSearch(event.target.value);
                            }}
                          />
                        </div>
                      </li>
                      <li>
                        <div className="drodown">
                          <Link
                            to="#status"
                            className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                            data-toggle="dropdown"
                          >
                            {status}
                          </Link>
                          <div className="dropdown-menu dropdown-menu-right">
                            <ul className="link-list-opt no-bdr">
                              <li>
                                <Link
                                  to="#status"
                                  onClick={() => setStatus('Todos')}
                                >
                                  <span>Todos</span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="#active"
                                  onClick={() => setStatus('Ativo')}
                                >
                                  <span>Ativo</span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="#inactive"
                                  onClick={() => setStatus('Inativo')}
                                >
                                  <span>Inativo</span>
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="nk-block">
            <div className="card card-bordered">
              <div className="card-inner-group">
                <div className="card-inner p-0">
                  <div className="nk-tb-list">
                    <div className="nk-tb-item nk-tb-head">
                      <div className="nk-tb-col">
                        <span>Nome</span>
                      </div>
                      <div className="nk-tb-col">
                        <span>Status</span>
                      </div>

                      <div className="nk-tb-col nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1 my-n1">
                          <li className="mr-n1" />
                        </ul>
                      </div>
                    </div>

                    {filteredCategories.map(item => (
                      <div className="nk-tb-item" key={item.uid}>
                        <div className="nk-tb-col">
                          <span className="tb-product">
                            <span className="title">{item.name}</span>
                          </span>
                        </div>

                        <div className="nk-tb-col">
                          <div className="asterisk tb-asterisk">
                            <span
                              className={`text-${
                                item.status ? 'success' : 'danger'
                              } fs-12px`}
                            >
                              {item.status && <span>Ativo</span>}
                              {!item.status && <span>Inativo</span>}
                            </span>
                          </div>
                        </div>
                        <div className="nk-tb-col nk-tb-col-tools">
                          <ul className="nk-tb-actions gx-1 my-n1">
                            <li className="mr-n1">
                              <div className="dropdown">
                                <Link
                                  to="#options"
                                  className="dropdown-toggle btn btn-icon btn-trigger"
                                  data-toggle="dropdown"
                                >
                                  <em className="icon ni ni-more-h" />
                                </Link>
                                <div className="dropdown-menu dropdown-menu-right">
                                  <ul className="link-list-opt no-bdr">
                                    <li>
                                      <Link
                                        to="#remove"
                                        onClick={() =>
                                          handleConfirmRemove(item.uid)
                                        }
                                      >
                                        <em className="icon ni ni-trash" />
                                        <span>Excluir</span>
                                      </Link>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default CategoriesComplements;
